import {StepperSelectionEvent} from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatStepper} from '@angular/material/stepper';
import {TranslateService} from '@ngx-translate/core';
import {
  RefModelStoreService,
  SnackBarService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {isEqual} from 'lodash';
import {BehaviorSubject, forkJoin, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';

import {CustomViewDaoService} from '../../../../../../core/services/custom-view-dao.service';
import {ICustomView, ICustomViewFilter} from '../../../../../models/custom-view.interface';
import {CustomViewService} from '../../../../../services/custom-view.service';
import {ViewState, ViewStateType} from '../../models/create-new-view.enum';
import {
  IColumnField,
  ISelectedColumnsEmit,
  ISelectedMetadata
} from '../../models/create-new-view.interface';

@Component({
  selector: 'app-edit-custom-view-dialog',
  styleUrls: ['./edit-custom-view-dialog.component.scss'],
  templateUrl: './edit-custom-view-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditCustomViewDialogComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnDestroy
{
  public title = 'RUBIC_CUBE.EDIT_METADATA_DIALOG_TITLE';
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public view$: BehaviorSubject<ViewStateType> = new BehaviorSubject<ViewStateType>(
    ViewState.SELECT_METADATA
  );

  public viewState = ViewState;
  public customView: ICustomView;

  @ViewChild('stepper') private stepper: MatStepper;

  /** The scope draft differs from the scope the available columns were fetched for. */
  private scopeDirty = false;
  private lastFetchedScope: ISelectedMetadata | null = null;

  private readonly stepOrder: ViewStateType[] = [
    ViewState.SELECT_METADATA,
    ViewState.SELECT_COLUMNS,
    ViewState.ADD_FILTERS
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<EditCustomViewDialogComponent>,
    private customViewService: CustomViewService,
    private translate: TranslateService,
    private changeRef: ChangeDetectorRef,
    private customViewDaoService: CustomViewDaoService,
    private rmeStore: RefModelStoreService,
    private snackBarService: SnackBarService
  ) {
    super();
  }

  ngOnInit(): void {
    this.fetchCustomView();
  }

  private fetchCustomView() {
    const refModel = this.rmeStore.refModel;
    this.customViewDaoService
      .getCustomView(refModel._id, this.data.viewId)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(
        response => {
          this.customView = response;
          this.seedEditState();
        },
        error => {
          this.snackBarService.error(error.error.error);
          this.closeModal();
        }
      );
  }

  /**
   * Load the metadata scope and available columns in parallel so all three steps
   * are ready for free navigation from the moment the dialog opens. Both requests
   * derive only from the already-fetched view, so they don't depend on each other.
   */
  private seedEditState() {
    const {
      refModel,
      metadataList,
      allNodes,
      name,
      description,
      private: isPrivate,
      filters
    } = this.customView;

    forkJoin([
      this.customViewDaoService.getCustomViewMetadataList(refModel),
      this.customViewDaoService.getAvailableColumnsList(refModel, {
        ...(!allNodes && {metadataList: metadataList || []}),
        allNodes
      })
    ])
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(
        ([metadata, availableColumns]) => {
          const {list, totalCount} = metadata;
          const scope = {selectedItems: metadataList, allNodes} as ISelectedMetadata;

          this.customViewService.storeRefModelMetadata({list, totalCount});
          this.customViewService.storeSelectedMetadata(scope);
          this.customViewService.storeGridName(name);
          this.customViewService.storeGridDescription(description);
          this.customViewService.storeIsPrivateGrid(isPrivate);
          // Persisted SIO filters use the collapsed `{column, subcolumn}` form; expand
          // them so display, pruning and the save mapping all round-trip correctly.
          this.customViewService.storeFilters(
            this.customViewService.parseSioFilters(filters || [])
          );
          this.customViewService.storeRefModelColumns(availableColumns);
          this.customViewService.setSelectedColumnFromCustomView(this.customView);

          this.lastFetchedScope = scope;
          this.scopeDirty = false;

          this.loading$.next(false);
          this.changeRef.detectChanges();
        },
        error => {
          this.snackBarService.error(error.error?.error);
          this.closeModal();
        }
      );
  }

  public onScopeStateChange({scope}: {scope: ISelectedMetadata; valid: boolean}): void {
    this.scopeDirty = !this.matchesFetchedScope(scope);
  }

  public goToColumnsStep(): void {
    this.enterStep(ViewState.SELECT_COLUMNS);
  }

  public storeSelectedColumns($event: ISelectedColumnsEmit) {
    this.customViewService.storeSelectedColumns($event);
    this.customViewService.pruneStoredFiltersToSelectedColumns();
    this.enterStep(ViewState.ADD_FILTERS);
  }

  public switchToMetadataView() {
    this.enterStep(ViewState.SELECT_METADATA);
  }

  public resetFilters() {
    this.enterStep(ViewState.SELECT_COLUMNS);
  }

  public onStepChange(event: StepperSelectionEvent): void {
    const state = this.stepOrder[event.selectedIndex];
    if (state && state !== this.view$.getValue()) {
      this.enterStep(state);
    }
  }

  /**
   * Single entry point for step navigation (Next buttons and stepper-header clicks
   * both land here): when the scope draft changed since the last fetch, refresh the
   * available columns and reconcile the current selection before rendering a
   * scope-dependent step.
   */
  private enterStep(state: ViewStateType): void {
    if (state === ViewState.SELECT_METADATA || !this.scopeDirty) {
      this.completeEnter(state);

      return;
    }

    const scope = this.customViewService.retrieveSelectedMetadata();

    this.loading$.next(true);

    this.customViewDaoService
      .getAvailableColumnsList(this.customView.refModel, {
        ...(!scope.allNodes && {metadataList: scope.selectedItems || []}),
        allNodes: scope.allNodes
      })
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(availableColumns => {
        this.customViewService.storeRefModelColumns(availableColumns);
        this.reconcileSelectionWithAvailable(availableColumns);

        this.lastFetchedScope = scope;
        this.scopeDirty = false;
        this.loading$.next(false);
        this.completeEnter(state);
      });
  }

  /** Keep the session's column selection, dropping entries the new scope no longer offers. */
  private reconcileSelectionWithAvailable(availableColumns: IColumnField[]): void {
    const availableValues = new Set(
      this.customViewService.flatSubcolumns(availableColumns).map(column => column.value)
    );
    const selected = this.customViewService.retrieveSelectedColumns().selectedColumns || [];

    this.customViewService.storeSelectedColumns({
      selectedColumns: selected.filter(column => availableValues.has(column.field?.value))
    });
    this.customViewService.pruneStoredFiltersToSelectedColumns();
  }

  private completeEnter(state: ViewStateType): void {
    this.view$.next(state);

    const target = this.stepOrder.indexOf(state);
    if (this.stepper && target !== -1 && this.stepper.selectedIndex !== target) {
      this.stepper.selectedIndex = target;
    }

    this.changeRef.detectChanges();
  }

  private matchesFetchedScope(scope: ISelectedMetadata): boolean {
    return (
      !!this.lastFetchedScope &&
      scope.allNodes === this.lastFetchedScope.allNodes &&
      isEqual(scope.selectedItems, this.lastFetchedScope.selectedItems)
    );
  }

  public submitFilters(emittedFilters: ICustomViewFilter[]) {
    this.loading$.next(true);

    const {refModel, _id, name, description} = this.customView;
    const {selectedItems, allNodes} = this.customViewService.retrieveSelectedMetadata();
    const gridName = this.customViewService.retrieveGridName();
    const gridDescription = this.customViewService.retrieveGridDescription();
    const selectedColumns = this.customViewService.retrieveSelectedColumns().selectedColumns || [];

    const filters = this.customViewService.mapFiltersForSio(emittedFilters);
    const filtersChanged = !isEqual(filters, this.customView.filters);

    const payload = {
      columns: selectedColumns.map(column => column.field.value),
      ...(!allNodes && {metadataList: selectedItems}),
      ...(name !== gridName && {name: gridName}),
      ...(description !== gridDescription && {description: gridDescription}),
      allNodes
    };

    this.customViewDaoService
      .updateCustomView(refModel, _id, payload)
      .pipe(
        switchMap(updated => {
          if (!filtersChanged) {
            return of({view: updated, filtersFailed: false});
          }

          // The view update is already persisted; report a filters failure as
          // partial instead of pretending nothing was saved.
          return this.customViewDaoService.updateFilters(refModel, _id, filters).pipe(
            map(view => ({view, filtersFailed: false})),
            catchError(() => of({view: updated, filtersFailed: true}))
          );
        }),
        this.takeUntilComponentDestroy()
      )
      .subscribe(
        ({view, filtersFailed}) => {
          this.customViewService.updateCustomView(view);
          const msg = this.translate.instant(
            filtersFailed ? 'RUBIC_CUBE.EDITED_FILTERS_FAILURE' : 'RUBIC_CUBE.EDITED_SUCCESSFULLY',
            {name}
          );
          this.loading$.next(false);
          this.dialogRef.close({data: view, msg});
        },
        errorResponse => {
          const msg = this.translate.instant(
            errorResponse.error?.error || 'RUBIC_CUBE.EDITED_FAILURE',
            {name}
          );
          this.loading$.next(false);
          this.dialogRef.close({msg});
        }
      );
  }

  public closeModal() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.customViewService.resetModal();
  }
}
